/**
 * String utility functions
 */

/**
 * Extract initials from a name
 * Returns up to 2 uppercase letters from the first words
 *
 * @param name - The name to extract initials from
 * @returns Uppercase initials (max 2 characters)
 *
 * @example
 * getInitials("Trading Squad") // Returns "TS"
 * getInitials("My Group") // Returns "MG"
 * getInitials("Bitcoin") // Returns "B"
 * getInitials("") // Returns "??"
 * getInitials("  ") // Returns "??"
 */
export function getInitials(name: string): string {
  // Handle invalid input
  if (!name || typeof name !== 'string') {
    return '??';
  }

  // Trim and check if empty
  const trimmed = name.trim();
  if (trimmed.length === 0) {
    return '??';
  }

  // Split on whitespace and filter empty strings
  const words = trimmed.split(/\s+/).filter(Boolean);

  // Extract first letter from each word (up to 2 words)
  const initials = words
    .slice(0, 2) // Take first 2 words only
    .map(word => word[0]?.toUpperCase() || '')
    .filter(Boolean) // Remove empty strings
    .join('');

  // Return initials or fallback
  return initials.length > 0 ? initials : '??';
}

/**
 * Inline tests (verified during development)
 *
 * Test cases:
 * - getInitials("Trading Squad") === "TS" ✓
 * - getInitials("My Group") === "MG" ✓
 * - getInitials("Bitcoin Investors Club") === "BI" ✓
 * - getInitials("Bitcoin") === "B" ✓
 * - getInitials("A") === "A" ✓
 * - getInitials("") === "??" ✓
 * - getInitials("  ") === "??" ✓
 * - getInitials("  Trading   Squad  ") === "TS" ✓
 */
